/**
 * Модалка запроса на анализ менеджера (ADR-019, TM-5743cb35).
 *
 * Открывается при ≥1 выбранном разговоре в карточке сотрудника.
 * Содержит textarea для свободного запроса + кнопки готовых шаблонов
 * (из `GET /api/analysis_templates`). На submit делает POST в
 * `/api/employees/{id}/analyses` и закрывается с toast-уведомлением.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createManagerAnalysis, listAnalysisTemplates } from "@/api/lookups";
import type { UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ManagerAnalysisDialogProps {
  open: boolean;
  employeeId: UUID;
  selectedMeetingIds: UUID[];
  onClose: () => void;
  /** Уведомление об успехе для родителя (тост + invalidate history). */
  onSubmitted?: () => void;
}

export function ManagerAnalysisDialog({
  open,
  employeeId,
  selectedMeetingIds,
  onClose,
  onSubmitted,
}: ManagerAnalysisDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [queryText, setQueryText] = useState("");
  const [pickedTemplateId, setPickedTemplateId] = useState<UUID | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Сбрасываем форму при закрытии — следующее открытие начинает с чистого.
  useEffect(() => {
    if (!open) {
      setQueryText("");
      setPickedTemplateId(null);
      setError(null);
    }
  }, [open]);

  const templatesQ = useQuery({
    queryKey: ["analysis-templates", { activeOnly: true }],
    queryFn: () => listAnalysisTemplates({ includeInactive: false }),
    enabled: open,
  });

  const submitMut = useMutation({
    mutationFn: () =>
      createManagerAnalysis(employeeId, {
        meeting_ids: selectedMeetingIds,
        query_text: queryText || null,
        template_id: pickedTemplateId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["manager-analyses", employeeId],
      });
      onSubmitted?.();
      onClose();
    },
    onError: () => setError(t("managerAnalysis.dialog.submitError")),
  });

  function handleSubmit(): void {
    setError(null);
    if (!queryText.trim() && !pickedTemplateId) {
      setError(t("managerAnalysis.dialog.queryRequired"));
      return;
    }
    submitMut.mutate();
  }

  function pickTemplate(id: UUID, text: string): void {
    setPickedTemplateId(id);
    // Подставляем prompt_text в textarea — пользователь может отредактировать.
    setQueryText(text);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="manager-analysis-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("managerAnalysis.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("managerAnalysis.dialog.selectedCount", {
              count: selectedMeetingIds.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label
              htmlFor="ma-dialog-query"
              className="mb-1 block text-sm font-medium"
            >
              {t("managerAnalysis.dialog.queryLabel")}
            </label>
            <textarea
              id="ma-dialog-query"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder={t("managerAnalysis.dialog.queryPlaceholder")}
              rows={6}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="manager-analysis-query-textarea"
            />
          </div>

          {templatesQ.data !== undefined && templatesQ.data.length > 0 ? (
            <div>
              <p className="mb-1 text-sm font-medium">
                {t("managerAnalysis.dialog.templatesLabel")}
              </p>
              <div className="flex flex-wrap gap-2">
                {templatesQ.data.map((tpl) => (
                  <Button
                    key={tpl.id}
                    type="button"
                    variant={
                      pickedTemplateId === tpl.id ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => pickTemplate(tpl.id, tpl.prompt_text)}
                    data-testid={`manager-analysis-template-${tpl.id}`}
                  >
                    {tpl.title}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {templatesQ.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("managerAnalysis.dialog.templatesLoadError")}
            </p>
          ) : null}

          {error !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitMut.isPending}
            >
              {t("managerAnalysis.dialog.cancelButton")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitMut.isPending}
              data-testid="manager-analysis-submit"
            >
              {t("managerAnalysis.dialog.submitButton")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
